import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';

import { reportAxiosError } from '~/Utils';

import type { FetchState } from './types';

// Action types for reducer
type FetchInit = { type: 'FETCH_INIT' };
type FetchSuccess<T> = { type: 'FETCH_SUCCESS'; payload: T; httpStatus: number };
type FetchFailure = { type: 'FETCH_FAILURE'; httpStatus?: number | null };
type FetchAction<T> = FetchInit | FetchSuccess<T> | FetchFailure;

export async function fetchData<T>(
  url: string | null,
  config?: AxiosRequestConfig,
  shouldReportAxiosError: boolean = true,
  shouldOpenErrorDialog: boolean = true
): Promise<FetchAction<T>> {
  if (!url) {
    return { type: 'FETCH_INIT' };
  }

  try {
    const result = await axios.get<T>(url, config);
    return { type: 'FETCH_SUCCESS', payload: result.data, httpStatus: result.status };
  } catch (error) {
    if (shouldReportAxiosError) {
      // `shouldOpenErrorDialog` is the report-but-do-not-interrupt case: a fetch the user never asked
      // for (a probe on mount) still reaches `/api/v1/errors`, but its failure is not something to make
      // them dismiss a modal over. Note the dialog flag also gates `Sentry.captureException` in
      // `reportAxiosErrorBase`, so suppressing it leaves the server-side report as the record.
      reportAxiosError(error, shouldOpenErrorDialog);
    }
    const httpStatus = axios.isAxiosError(error) ? error.response?.status : null;
    return { type: 'FETCH_FAILURE', httpStatus };
  }
}

export const dataFetchReducer = <T>(state: FetchState<T>, action: FetchAction<T>): FetchState<T> => {
  switch (action.type) {
    case 'FETCH_INIT':
      return {
        ...state,
        isLoading: true,
        isError: false,
      };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isError: false,
        data: action.payload,
        httpStatus: action.httpStatus,
      };
    case 'FETCH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isError: true,
        httpStatus: action.httpStatus,
      };
    default:
      throw new Error();
  }
};
